import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import Logo from "@/components/Logo";
import ScanResults from "@/components/ScanResults";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, AlertCircle, Share2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import ProjectDisclaimer from "@/components/ProjectDisclaimer";
import type { VisibilityAnalysis } from "@/lib/api/ai-visibility";

const SharedScanPage = () => {
  const { scanId } = useParams<{ scanId: string }>();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scanData, setScanData] = useState<any>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [brandName, setBrandName] = useState("");
  const [keyword, setKeyword] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState<string | undefined>();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!scanId) {
      setError("No scan ID provided");
      setLoading(false);
      return;
    }

    // Basic UUID format check
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(scanId)) {
      setError("Invalid scan link");
      setLoading(false);
      return;
    }

    const fetchScan = async () => {
      const { data, error: fetchError } = await supabase
        .rpc("get_scan_by_id", { scan_id: scanId });

      if (fetchError) {
        console.error("Fetch scan error:", fetchError);
        setError("Failed to load scan results");
        setLoading(false);
        return;
      }

      const rows = data as any[];
      if (!rows || rows.length === 0) {
        setError("Scan not found");
        setLoading(false);
        return;
      }

      const row = rows[0];
      setScanData(row.scan_data);
      setCreatedAt(row.created_at);
      setBrandName(row.brand_name);
      setKeyword(row.keyword);
      setWebsiteUrl(row.website_url || undefined);
      setLoading(false);
    };

    fetchScan();
  }, [scanId]);

  if (loading) {
    return (
      <main className="min-h-screen bg-background flex flex-col items-center px-4 py-12">
        <Logo />
        <div className="w-full max-w-2xl mt-8 space-y-4">
          <Skeleton className="h-8 w-64 mx-auto" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen bg-background flex flex-col items-center justify-center px-4 gap-6">
        <Logo />
        <div className="text-center space-y-3">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto" />
          <h1 className="text-2xl font-bold">{error}</h1>
          <p className="text-muted-foreground">This scan link may have expired or doesn't exist.</p>
        </div>
        <Button asChild>
          <Link to="/">Run your own scan</Link>
        </Button>
      </main>
    );
  }

  // Map scan_data to ScanResults props — same mapping as ScanForm.handleSubmit
  const d = scanData;

  const analysis: VisibilityAnalysis = {
    visibilityScore: d.overallAnalysis?.visibilityScore || 0,
    keyFindings: d.overallAnalysis?.keyFindings || [],
    competitorMentions: d.overallAnalysis?.competitorMentions || [],
    recommendations: d.overallAnalysis?.recommendations || [],
    summary: d.overallAnalysis?.summary || "",
  };

  const platformResults = d.platformAnalysis?.platforms || [];

  // Extract Google result if saved
  const savedGoogle = d.googleResult;
  const googleResult = savedGoogle ? {
    success: true,
    brandName: brandName,
    platform: 'Google Search' as const,
    found: savedGoogle.found,
    position: savedGoogle.position,
    context: savedGoogle.context,
    confidence: savedGoogle.confidence,
    topCompetitors: savedGoogle.topCompetitors,
    domainVerified: savedGoogle.domainVerified,
    spellingCorrected: savedGoogle.spellingCorrected,
    suggestedQuery: savedGoogle.suggestedQuery,
  } : null;

  const formattedDate = createdAt
    ? new Date(createdAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <>
      <Helmet>
        <title>{brandName && keyword ? `${brandName} for "${keyword}" — Shared AI Visibility Scan | AILens` : "Shared AI Visibility Scan | AILens"}</title>
        <meta name="description" content={brandName && keyword ? `Shared AI visibility scan for ${brandName} on "${keyword}". ChatGPT discovery and Google top 10 position from AILens research scan.` : "Shared AI visibility scan from AILens. ChatGPT discovery and Google top 10 position on a 0 to 100 research scale."} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.origin + `/scan/${scanId}` : ''} />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.origin + `/scan/${scanId}` : ''} />
        <meta property="og:title" content={brandName ? `AI Visibility Results for ${brandName}` : "AI Visibility Scan Results"} />
        <meta property="og:description" content={brandName && keyword ? `See how ChatGPT and Google rank ${brandName} for "${keyword}". Research-based AI visibility scan from AILens.` : "Research-based AI visibility scan results from AILens."} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={brandName ? `AI Visibility Results for ${brandName}` : "AI Visibility Scan Results"} />
        <meta name="twitter:description" content={brandName && keyword ? `How ChatGPT and Google rank ${brandName} for "${keyword}".` : "Research-based AI visibility scan results."} />
      </Helmet>
      <main className="min-h-screen bg-background bg-dot-grid flex flex-col items-center px-4 py-0 pb-24 md:pb-12 relative overflow-hidden">
        <h1 className="sr-only">{brandName && keyword ? `Shared AI Visibility Scan Results for ${brandName} on "${keyword}"` : "Shared AI Visibility Scan Results"}</h1>
        <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-sm border-b border-border/40 shadow-sm mb-6">
          <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              New Scan
            </Link>
            <a href="/" className="hover:opacity-80 transition-opacity">
              <Logo />
            </a>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                setCopied(true);
                toast({ title: "Link copied!", description: "Share this link with anyone." });
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {copied ? "Copied" : "Share"}
            </Button>
          </div>
        </header>

        <div className="relative z-10 flex flex-col items-center gap-6 w-full">
          {formattedDate && (
            <p className="text-sm text-muted-foreground">Scanned on {formattedDate}</p>
          )}

          <ScanResults
            analysis={analysis}
            brandName={brandName}
            keyword={keyword}
            platformResults={platformResults}
            googleResult={googleResult}
            websiteUrl={websiteUrl}
            chatGptStatus="success"
            googleStatus="success"
            isFromCache={false}
            aiScore={d.aiScore}
            queryType={d.queryType}
            queryUsed={d.queryUsed}
            rawResponseSnippet={d.rawResponseSnippet}
            competitorMentions={d.competitorMentions}
          />

          <p className="text-muted-foreground text-sm text-center animate-fade-in mt-8" style={{ animationDelay: "0.5s" }}>
            AILens © 2026 ·{" "}
            <a href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</a>
            {" "}·{" "}
            <a href="/terms" className="hover:text-foreground transition-colors">Terms of Service</a>
          </p>
          <ProjectDisclaimer />
        </div>
      </main>
    </>
  );
};

export default SharedScanPage;
